/**
 * Resolves a human-readable label for a form element, used in validation error messages.
 *
 * Resolution order:
 * 1. data-label attribute on the element (explicit override, used for checkbox groups)
 * 2. Enclosing <label> text content (common for checkbox/radio inside <label>)
 * 3. <label for="id"> text content
 * 4. Previous sibling <td> text (standard two-column table layout)
 * 5. Fall back to the raw identifier (name / id / group name)
 */
export function getFieldLabel(el: Element, fallback: string): string {
  // 1. Explicit data-label
  const dataLabel = el.getAttribute('data-label');
  if (dataLabel?.trim()) return dataLabel.trim();

  // 2. Enclosing <label> element (e.g. <label><input .../> Text</label>)
  const parentLabel = el.closest('label');
  if (parentLabel) {
    const text = parentLabel.textContent?.trim().replace(/\s+/g, ' ');
    if (text) return text;
  }

  // 3. <label for="id"> association
  const elId = el.getAttribute('id');
  if (elId) {
    const associated = el.ownerDocument?.querySelector(`label[for="${elId}"]`);
    if (associated) {
      const text = associated.textContent?.trim().replace(/\s+/g, ' ');
      if (text) return text;
    }
  }

  // 4. Previous sibling <td> in the same <tr> (two-column table layout)
  const parentTd = el.closest('td');
  if (parentTd) {
    const prev = parentTd.previousElementSibling;
    if (prev?.tagName === 'TD') {
      const text = prev.textContent?.trim().replace(/:\s*$/, '').trim();
      if (text) return text;
    }
  }

  return fallback;
}

/**
 * Resolves the label for a required-checkbox-group from the parsed document.
 * Returns the data-label of any checkbox in the group, or the raw group name.
 */
export function getGroupLabel(doc: Document, groupName: string): string {
  const candidates = doc.querySelectorAll('input[data-required-group]');
  const el = Array.from(candidates).find(
    (input) => input.getAttribute('data-required-group') === groupName,
  );
  if (el) {
    const dataLabel = el.getAttribute('data-label');
    if (dataLabel?.trim()) return dataLabel.trim();
  }
  return groupName;
}
